import React from 'react';
import { FieldRenderProps } from '@progress/kendo-react-form';

export interface FlexibleScheduleValue {
  averageWeeklyHours: string;
  scheduleDescription: string;
}

export interface FlexibleScheduleProps
  extends Omit<Partial<FieldRenderProps>, 'onChange' | 'value'> {
  onChange: (e: { value: FlexibleScheduleValue }) => void;
  value?: FlexibleScheduleValue;
  processedProps?: boolean;
}

const MAX_HOURS = 168; // 7 × 24
const MAX_ERROR_TEXT = `Max value: ${MAX_HOURS} hrs.`;

const INPUT_BASE =
  'tw-w-[70px] tw-h-8 tw-text-center tw-text-base ' +
  'tw-bg-unm-bg-light tw-border tw-rounded-lg tw-outline-none';
const INPUT_ENABLED =
  'tw-border-unm-border1 focus:tw-border-unm-interactive01-400 tw-cursor-text';
const INPUT_ERROR =
  'tw-border-2 tw-border-[#B42318] focus:tw-border-[#B42318] tw-cursor-text';

const TEXTAREA_BASE =
  'tw-w-full tw-min-h-[102px] tw-p-2.5 tw-text-base ' +
  'tw-bg-unm-bg-light tw-border tw-border-unm-border1 tw-rounded-lg ' +
  'tw-outline-none tw-resize-y focus:tw-border-unm-interactive01-400';

const inputClass = (error: boolean) =>
  `${INPUT_BASE} ${INPUT_ENABLED} ${error ? INPUT_ERROR : ''}`;

const isOver = (v: string) =>
  v !== '' &&
  !Number.isNaN(Number.parseInt(v)) &&
  Number.parseInt(v) > MAX_HOURS;

export function FlexibleSchedule({
  onChange,
  value,
  onBlur,
  processedProps,
}: Readonly<FlexibleScheduleProps>) {
  const [averageWeeklyHours, setAverageWeeklyHours] = React.useState(
    value?.averageWeeklyHours ?? '',
  );
  const [scheduleDescription, setScheduleDescription] = React.useState(
    value?.scheduleDescription ?? '',
  );
  const [validationError, setValidationError] = React.useState('');

  React.useEffect(() => {
    if (!processedProps) return;
    const hrs = value?.averageWeeklyHours ?? '';
    const desc = value?.scheduleDescription ?? '';
    setAverageWeeklyHours(hrs);
    setScheduleDescription(desc);
    setValidationError('');
    onChange({ value: { averageWeeklyHours: hrs, scheduleDescription: desc } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedProps]);

  const onHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (
      v !== '' &&
      !Number.isNaN(Number.parseInt(v)) &&
      Number.parseInt(v) > MAX_HOURS
    ) {
      setValidationError(MAX_ERROR_TEXT);
    } else {
      setValidationError('');
    }
    setAverageWeeklyHours(v);
    onChange({ value: { averageWeeklyHours: v, scheduleDescription } });
  };

  const onDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const desc = e.target.value;
    setScheduleDescription(desc);
    onChange({ value: { averageWeeklyHours, scheduleDescription: desc } });
  };

  const handleBlur = (e: React.FocusEvent<HTMLFieldSetElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      onBlur?.();
    }
  };

  return (
    <fieldset
      id="form-variable-flexible-schedule"
      className="tw-w-full tw-border-0 tw-p-0 tw-m-0"
      onBlur={handleBlur}
    >
      <div className="tw-bg-white tw-shadow-[0px_2px_4px_0px_rgba(2,84,126,0.20)] tw-rounded-[8px] tw-px-5 tw-py-8 tw-flex tw-flex-col tw-gap-5">
        <p className="tw-text-base tw-text-unm-adhoc2">
          Since both your hours and days can change week to week, give us a
          sense of your typical weekly commitment. We need an average to work
          with. An estimate is perfectly fine if you&apos;re not sure of the
          exact figure.
        </p>

        {/* Average weekly hours */}
        <div className="tw-flex tw-flex-col tw-gap-3 tw-px-5 tw-py-2">
          <p className="tw-text-base tw-font-semibold tw-text-unm-adhoc2">
            Average weekly hours (over the last year)
          </p>
          <div className="tw-flex tw-items-center tw-gap-3">
            <input
              type="number"
              id="form-variable-flexible-schedule-hours"
              min={0}
              max={MAX_HOURS}
              value={averageWeeklyHours}
              placeholder="e.g. 30"
              onChange={onHoursChange}
              className={inputClass(isOver(averageWeeklyHours))}
              aria-label="Average weekly hours"
            />
            <span className="tw-text-base tw-text-unm-adhoc2">
              hrs/week (estimated average)
            </span>
          </div>
        </div>

        {/* Schedule description */}
        <div className="tw-flex tw-flex-col tw-gap-3 tw-px-5 tw-py-2">
          <p className="tw-text-base tw-font-semibold tw-text-unm-adhoc2">
            Describe how your schedule typically works
          </p>
          <textarea
            id="form-variable-flexible-schedule-description"
            value={scheduleDescription}
            placeholder='e.g. "My hours vary between 20 and 45 per week depending on the season."'
            onChange={onDescriptionChange}
            className={TEXTAREA_BASE}
            aria-label="Schedule description"
          />
        </div>

        {validationError && (
          <p
            id="form-variable-flexible-schedule-error"
            className="tw-text-sm tw-font-semibold tw-text-[#B42318]"
          >
            {validationError}
          </p>
        )}
      </div>
    </fieldset>
  );
}
